import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { DatePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { useClientView } from "../../../components/TimeTracker/TimeTrackerClient/useClientDetails";
import { useFetchProjectDetails } from "../../../components/TimeTracker/TimeTrackerProject/useProjectDetails";
import SearchIcon from "../../../images/icons/search-icon.svg";
import CalendarIcon from "../../../images/icons/calendar-icon.svg";
import LeftArrow from "../../../images/icons/blue-left-arrow.svg";
import {
  GetRoi,
  TotalBudget,
  TotalBudgetedHours,
  TotalCost,
  TrackedProjectHours,
} from "../../../helpers/ProjectHelpers.jsx";
import NewProjectsModal from "../ClientProject/helpers/NewProjectsModal";
import MvpClientChrome from "./MvpClientChrome";
import "./MvpClientProjectList.css";

const PAGE_SIZES = [6, 10, 20];

function clientIdOf(row) {
  const value = row?.client_name;
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || "";
}

function metrics(row) {
  const sprints = Array.isArray(row?.sprints) ? row.sprints : [];
  const timers = Array.isArray(row?.timers) ? row.timers : [];
  const sprintBudget = sprints.reduce((sum, sprint) => sum + (Number(sprint.budget) || 0), 0);
  const budget = sprintBudget || Number(row?.budget) || 0;
  const cost = timers.reduce((sum, timer) => sum + (Number(timer.totalCost) || 0), 0);
  const budgetedHours = sprints.reduce((sum, sprint) => sum + (Number(sprint.totalHours) || 0), 0);
  const trackedSeconds = timers.reduce((sum, timer) => sum + (Number(timer.duration) || 0), 0);
  let roi = "0%";
  if (cost > 0) roi = `${Math.round(((budget - cost) / cost) * 100)}%`;
  else if (budget > 0) roi = "N/A";
  return { budget, cost, budgetedHours, trackedSeconds, roi };
}

function statusOf(row) {
  if (row?.is_archived) return "Completed";
  if (row?.is_active === false) return "Inactive";
  return "Active";
}

function HoverText({ text }) {
  const clipRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const value = text || "—";

  useEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [value]);

  return (
    <span className="mvp-cp-hover">
      <span ref={clipRef} className="mvp-cp-hover-clip">{value}</span>
      {truncated && <span className="mvp-cp-hover-full">{value}</span>}
    </span>
  );
}

function SortIcon() {
  return (
    <span className="mvp-cp-sort" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path d="M0.424316 3.64275L3.21843 0.848633L6.01255 3.64275" strokeWidth="1.2" stroke="#405BFF" />
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path d="M6.0127 0.424632L3.21858 3.21875L0.42446 0.424632" strokeWidth="1.2" stroke="#405BFF" />
      </svg>
    </span>
  );
}

function pageItems(current, total) {
  if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
}

function FilterSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <label className="mvp-cp-field" onBlur={() => setOpen(false)}>
      <span>{label}</span>
      <button type="button" className="mvp-cp-trigger" onClick={() => setOpen((current) => !current)}>
        <span>{options.find((option) => option.id === value)?.label || "All"}</span>
        <svg width="11" height="5" viewBox="0 0 11 5" aria-hidden="true">
          <path d="M1 1L5.5 4.2L10 1" fill="none" stroke="#405BFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="mvp-cp-menu">
          {options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className={option.id === value ? "is-active" : ""}
                onMouseDown={(event) => {
                  event.preventDefault();
                  onChange(option.id);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </label>
  );
}

export default function MvpClientProjectList({ clientId, encryptedId }) {
  const token = useSelector((state) => state.auth.token);
  const projectAdded = useSelector((state) => state.project?.projectAdded);
  const { data: client } = useClientView(clientId, token);
  const { data: projects = [], isLoading, refetch } = useFetchProjectDetails(token);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [createdOn, setCreatedOn] = useState("");
  const [pageSize, setPageSize] = useState(6);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: "created", dir: "desc" });
  const [openSignal, setOpenSignal] = useState(0);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => {
    if (projectAdded) refetch();
  }, [projectAdded, refetch]);

  const encoded = encodeURIComponent(encryptedId || "");

  const rows = useMemo(() => {
    const list = (Array.isArray(projects) ? projects : []).filter((row) => {
      if (row?.is_delete) return false;
      if (String(clientIdOf(row)) !== String(clientId)) return false;
      const name = (row.project_name || "").toLowerCase();
      if (query && !name.includes(query.trim().toLowerCase())) return false;
      const rowStatus = statusOf(row);
      if (status === "Active" && rowStatus !== "Active") return false;
      if (status === "Inactive" && rowStatus !== "Inactive") return false;
      if (status === "Archived" && !row.is_archived) return false;
      if (status === "Completed" && rowStatus !== "Completed") return false;
      if (createdOn && moment(row.created_at).format("YYYY-MM-DD") !== createdOn) return false;
      return true;
    });

    const valueOf = (row) => {
      const stats = metrics(row);
      if (sort.key === "name") return (row.project_name || "").toLowerCase();
      if (sort.key === "status") return statusOf(row);
      if (sort.key === "roi") return stats.roi;
      if (sort.key === "budget") return stats.budget;
      if (sort.key === "hours") return stats.budgetedHours;
      if (sort.key === "cost") return stats.cost;
      if (sort.key === "tracked") return stats.trackedSeconds;
      if (sort.key === "billable") return row.is_billable === false ? 0 : 1;
      return new Date(row.created_at || 0).getTime();
    };

    return list.sort((a, b) => {
      const left = valueOf(a);
      const right = valueOf(b);
      if (left < right) return sort.dir === "asc" ? -1 : 1;
      if (left > right) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [projects, clientId, query, status, createdOn, sort, projectAdded]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visible = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    const tableEl = tableRef.current;
    const contentOuter = document.querySelector(".content-outer.clients");
    if (!tableEl || !contentOuter) return undefined;

    let atEnd = false;
    const updatePadding = () => {
      if (window.innerWidth > 700) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
        return;
      }
      const maxScroll = Math.max(0, tableEl.scrollWidth - tableEl.clientWidth);
      if (maxScroll <= 2) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
        return;
      }
      if (!atEnd && tableEl.scrollLeft >= maxScroll - 4) {
        atEnd = true;
        contentOuter.classList.add("mvp-hscroll-end");
      } else if (atEnd && tableEl.scrollLeft < maxScroll - 24) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
      }
    };

    tableEl.addEventListener("scroll", updatePadding, { passive: true });
    window.addEventListener("resize", updatePadding);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePadding) : null;
    resizeObserver?.observe(tableEl);
    updatePadding();

    return () => {
      tableEl.removeEventListener("scroll", updatePadding);
      window.removeEventListener("resize", updatePadding);
      resizeObserver?.disconnect();
      contentOuter.classList.remove("mvp-hscroll-end");
    };
  }, [visible.length]);

  const toggleSort = (key) => {
    setSort((current) => (
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    ));
    setPage(1);
  };

  const headers = [
    ["name", "Project"],
    ["status", "Status"],
    ["roi", "ROI"],
    ["budget", "Budget"],
    ["hours", "Budgeted Hours"],
    ["cost", "Cost"],
    ["tracked", "Tracked Hours"],
    ["created", "Date Created"],
    ["billable", "Billable"],
  ];

  return (
    <MvpClientChrome client={client} encryptedId={encryptedId}>
      <div className="mvp-cp">
        <Link className="mvp-cp-back" to="/client/overview">
          <img src={LeftArrow} alt="" />
          Back to All Clients
        </Link>
        <div className="mvp-cp-body">
        <div className="mvp-cp-toolbar">
          <button type="button" className="mvp-cp-new" onClick={() => setOpenSignal((value) => value + 1)}>
            <span>+</span>
            New Project
          </button>
          <label className="mvp-cp-search">
            <input
              type="search"
              value={query}
              placeholder="Search by project name"
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              style={{ backgroundImage: `url(${SearchIcon})` }}
            />
          </label>
        </div>

        <div className={`mvp-cp-filters ${filtersOpen ? "is-open" : ""}`}>
          <FilterSelect
            label="Project Status"
            value={status}
            onChange={(next) => {
              setStatus(next);
              setPage(1);
            }}
            options={[
              { id: "all", label: "All" },
              { id: "Active", label: "Active" },
              { id: "Inactive", label: "Inactive" },
              { id: "Archived", label: "Archived" },
              { id: "Completed", label: "Completed" },
            ]}
          />
          <label className="mvp-cp-field">
            <span>Date Created</span>
            <span className="mvp-cp-date">
              <DatePicker
                oneTap
                format="MM/dd/yy"
                placeholder="mm/dd/yy"
                placement="bottomStart"
                container={() => document.body}
                value={createdOn ? moment(createdOn, "YYYY-MM-DD").toDate() : null}
                onChange={(date) => {
                  setCreatedOn(date ? moment(date).format("YYYY-MM-DD") : "");
                  setPage(1);
                }}
              />
              <img className="mvp-cp-date-icon" src={CalendarIcon} alt="" />
            </span>
          </label>
          <div className="mvp-cp-count" onBlur={() => setSizeOpen(false)}>
            <span>Showing</span>
            <button type="button" className="mvp-cp-size" onClick={() => setSizeOpen((current) => !current)}>
              {pageSize}
              <svg width="9" height="5" viewBox="0 0 11 5" aria-hidden="true">
                <path d="M1 1L5.5 4.2L10 1" fill="none" stroke="#405BFF" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            </button>
            {sizeOpen && (
              <ul className="mvp-cp-menu mvp-cp-size-menu">
                {PAGE_SIZES.map((size) => (
                  <li key={size}>
                    <button
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setPageSize(size);
                        setPage(1);
                        setSizeOpen(false);
                      }}
                    >
                      {size}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <span>of {rows.length} results</span>
          </div>
        </div>

        <div className="mvp-cp-card">
          <div className="mvp-cp-mobile-head">
            <h2>All Projects</h2>
            <button type="button" className={filtersOpen ? "is-open" : ""} onClick={() => setFiltersOpen((open) => !open)}>
              Filters
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M2 4.5h12M4.5 8h7M6.5 11.5h3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                <circle cx="5" cy="4.5" r="1.3" fill="#fff" stroke="currentColor" strokeWidth="1.2" />
                <circle cx="11" cy="8" r="1.3" fill="#fff" stroke="currentColor" strokeWidth="1.2" />
                <circle cx="7.5" cy="11.5" r="1.3" fill="#fff" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </button>
          </div>
        <div className="mvp-cp-table-wrap" ref={tableRef}>
          <table className="mvp-cp-table">
            <thead>
              <tr>
                {headers.map(([key, label]) => (
                  <th key={key}>
                    <button type="button" onClick={() => toggleSort(key)}>
                      {key === "name" ? (
                        <>
                          <span className="mvp-cp-h-desk">Project</span>
                          <span className="mvp-cp-h-mob">Project Name</span>
                        </>
                      ) : label}
                      <SortIcon />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td className="mvp-cp-empty" colSpan={9}>Loading projects…</td>
                </tr>
              )}
              {!isLoading && visible.length === 0 && (
                <tr>
                  <td className="mvp-cp-empty" colSpan={9}>No projects found</td>
                </tr>
              )}
              {!isLoading && visible.map((row) => {
                const statusName = statusOf(row);
                const billable = row.is_billable !== false;
                const safe = { ...row, sprints: row.sprints || [], timers: row.timers || [] };
                return (
                  <tr key={row._id}>
                    <td>
                      <span className="mvp-cp-name">
                        <HoverText text={row.project_name || "Project"} />
                        <Link className="mvp-cp-go" to={`/client/projects/details/${row._id}/${encoded}`} aria-label="Open project">
                          <svg width="8" height="12" viewBox="0 0 8 12" aria-hidden="true">
                            <path d="M1.4 1.2 6.2 6 1.4 10.8" fill="none" stroke="#98A2B3" strokeWidth="1.4" strokeLinecap="round" />
                          </svg>
                        </Link>
                      </span>
                    </td>
                    <td>
                      <div className="mvp-cp-status-cell">
                        <span className={`mvp-cp-pill ${statusName === "Active" ? "is-active" : "is-muted"}`}>{statusName}</span>
                        <Link className="mvp-cp-details" to={`/client/projects/details/${row._id}/${encoded}`}>Details</Link>
                      </div>
                    </td>
                    <td><GetRoi data={safe} /></td>
                    <td><TotalBudget data={safe} /></td>
                    <td><TotalBudgetedHours data={safe} /></td>
                    <td><TotalCost data={safe} timers={safe.timers} /></td>
                    <td><TrackedProjectHours timers={safe.timers} /></td>
                    <td>{row.created_at ? moment(row.created_at).format("MMMM DD, YYYY") : "—"}</td>
                    <td>
                      <span className={`mvp-cp-bill ${billable ? "is-yes" : "is-no"}`}>{billable ? "Yes" : "No"}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>

        <div className="mvp-cp-pagination">
          <button type="button" className="mvp-cp-page-nav" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M7.8 2.4 4.2 6l3.6 3.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            Back
          </button>
          {pageItems(currentPage, totalPages).map((item) => (
            typeof item === "number" ? (
              <button
                key={item}
                type="button"
                className={item === currentPage ? "mvp-cp-page is-current" : "mvp-cp-page"}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ) : (
              <span key={item} className="mvp-cp-page">...</span>
            )
          ))}
          <button type="button" className="mvp-cp-page-nav" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>
            Next
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M4.2 2.4 7.8 6 4.2 9.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <NewProjectsModal hideButton openSignal={openSignal} lockedClientId={clientId} />
        </div>
      </div>
    </MvpClientChrome>
  );
}
